import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { Button } from '@/components/ui/Button';
import { LiveRegion } from '@/components/ui/LiveRegion';

interface ChangeMarkersProps {
  /** The page holds edits Save Draft has not saved yet. */
  unsaved: boolean;
  /** The live ARC in the app differs from the saved draft. */
  unpublished: boolean;
}

/**
 * Beside the page's title, as the wireframe draws it (Part 2, page 4):
 * "Unsaved Changes" means the page holds edits Save Draft has not sent;
 * "Unpublished Changes" means the saved draft differs from what the app
 * shows. Both can show at once. Each marker's appearance is announced,
 * because nothing else on the page says so.
 */
export function ChangeMarkers({ unsaved, unpublished }: ChangeMarkersProps) {
  const said = [
    unsaved ? 'Unsaved changes' : '',
    unpublished ? 'Unpublished changes' : '',
  ]
    .filter(Boolean)
    .join('. ');
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      <LiveRegion message={said} />
      {unsaved ? (
        <Typography variant="body2" color="error" data-testid="arc-builder-unsaved-chip">
          Unsaved Changes
        </Typography>
      ) : null}
      {unpublished ? (
        <Typography
          variant="body2"
          color="warning.dark"
          data-testid="arc-builder-unpublished-chip"
        >
          Unpublished Changes
        </Typography>
      ) : null}
    </Box>
  );
}

interface PublishBarProps {
  /** The page holds edits Save Draft has not saved yet. */
  unsaved: boolean;
  /** Nothing can be saved or published: loading, failed, read-only or busy. */
  disabled: boolean;
  saving: boolean;
  publishing: boolean;
  /**
   * Preview can be pressed: the ARC has loaded and no write is on its way.
   * Read-only viewers may preview too, since it changes nothing (D16).
   */
  canPreview: boolean;
  onSave: () => void;
  onPublish: () => void;
  onPreview: () => void;
}

/**
 * The buttons at the top right of the ARC Builder (E.14, decided by Sebin on
 * 6 October: no autosave). Save Draft is offered only while there is
 * something to save. Preview (group 7) shows the working copy as the app
 * will, unsaved edits included.
 */
export function PublishBar({
  unsaved,
  disabled,
  saving,
  publishing,
  canPreview,
  onSave,
  onPublish,
  onPreview,
}: PublishBarProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        flexWrap: 'wrap',
        alignItems: 'center',
        width: { xs: '100%', sm: 'auto' },
      }}
    >
      <Button
        variant="secondary"
        onClick={onPreview}
        disabled={!canPreview}
        data-testid="arc-builder-preview-button"
      >
        Preview
      </Button>
      <Button
        variant="secondary"
        onClick={onSave}
        disabled={disabled || !unsaved || publishing}
        loading={saving}
        data-testid="arc-builder-save-draft-button"
      >
        Save Draft
      </Button>
      <Button
        variant="primary"
        onClick={onPublish}
        disabled={disabled || saving}
        loading={publishing}
        data-testid="arc-builder-publish-button"
      >
        Publish Changes
      </Button>
    </Box>
  );
}
